// /github-auth-callback (and /auth/callback as a generic fallback) —
// completes a GitHub OAuth handshake against the SYMBOLS SDK.
//
// Flow:
//   1. GitHub redirects here with `?code=<...>` after the user authorizes.
//   2. We pull the invite token out of localStorage (it was stashed by
//      the OAuth click handler in LoginWindow because GitHub's redirect
//      strips arbitrary query params).
//   3. `el.sdk.githubAuth(code, inviteToken)` POSTs to /auth/github on
//      the Symbols server, which exchanges the code with GitHub's API,
//      creates/finds the user in MongoDB, and returns a Symbols SDK
//      session (TokenManager auto-persists the access + refresh tokens).
//   4. `initializeUserSession` hydrates the in-memory user via the SDK.
//   5. Route to `?next=` (read from URL or localStorage stash) or `/`.
//
// Errors surface inline so an OAuth misconfig (provider disabled,
// callback URL mismatch, etc.) is visible instead of a black screen.
//
// EVERY status this page shows is a bundle KEY in `s.statusKey`, resolved at
// render time — never a literal. This line of text IS the whole page while
// the handshake runs, so an English literal here left a Georgian arrival
// reading English from the first frame to the last, and no key gate could see
// it: the PROP check reads `text: '<literal>'`, and these sat behind a `||`
// inside a reactive function or under a plain state key. A failure is
// classified to a CODE by `functions/authCallbackError.js`; the provider's
// own words go to the console, never to the screen.
// (DESIGN-AUTH-CALLBACK-STATUS-TEXT-IS-ENGLISH-ONLY-ON-A-GEORGIAN-ARRIVAL-1;
// the gate is `pages/auth/callbackStatusI18n.test.js` plus this file's entry
// in `__tests__/i18nSharedExtraction.test.js` BRANCH_CLEAR.)

import { readInviteToken } from '../../functions/inviteToken.js'
import { consumeOAuthNext } from '../../functions/postOAuthNext.js'

export const callback = {
  extends: 'Page',
  metadata: (el) => ({ title: el.call('polyglot', 'auth.page.callback.title') }),
  flow: 'y',
  align: 'center center',
  padding: 'D C',
  width: '100%',
  height: '100dvh',
  maxWidth: 'none',

  Card: {
    flow: 'column',
    align: 'center center',
    gap: 'A',
    padding: 'C',
    round: 'radiusCard',
    theme: 'dialog',
    border: 'hairline solid blue.1',
    minWidth: 'F',

    Status: {
      tag: 'p',
      fontSize: 'A',
      // `statusKey` is a bundle KEY, never copy — see the note above.
      text: (el, s) => el.call('polyglot', s.statusKey || 'auth.callback.signingIn'),
      color: (el, s) => (s.error ? 'red' : 'fg.0')
    },

    onRender: async (el, s) => {
      // Idempotency guard — onRender can fire twice in dev under HMR.
      if (s.exchangeStarted) return
      s.update({
        exchangeStarted: true,
        statusKey: 'auth.callback.verifyingGithub',
        error: false
      })

      const url = new URL(window.location.href)
      const code = url.searchParams.get('code')
      // The CODE classifies (`access_denied`, `bad_verification_code`); the
      // description is prose. Both reach the console, neither reaches the
      // screen — they are English and in no bundle.
      const errorCode = url.searchParams.get('error')
      const errorDetail = url.searchParams.get('error_description')
      if (errorCode || errorDetail) {
        console.error('[github-auth-callback] provider refused:', errorCode, errorDetail)
        s.update({
          error: true,
          statusKey: el.call('authCallbackErrorKey', errorCode || errorDetail)
        })
        return
      }
      if (!code) {
        s.update({ error: true, statusKey: 'auth.callback.error.missingCode' })
        return
      }

      try {
        const inviteToken = readInviteToken('github')
        const loginResult = await el.sdk.githubAuth(code, inviteToken)
        // A sign-in on this origin: its refresh token is its own (review D1).
        el.call('markOwnSession')

        s.update({ statusKey: 'auth.callback.hydrating' })
        await el.call('initializeUserSession', { loginData: loginResult })

        s.update({ statusKey: 'auth.callback.redirecting' })
        const next = consumeOAuthNext(url.searchParams.get('next'))
        // SIGNUP-INTENT — see functions/signupIntent.js
        window.location.replace(next === '/' ? el.call('takeSignupCheckoutNext') || next : next)
      } catch (err) {
        console.error('[github-auth-callback] failed:', err)
        s.update({
          error: true,
          statusKey: el.call('authCallbackErrorKey', err)
        })
      }
    },

    BackLink: {
      extends: 'Link',
      href: '/login',
      text: '{{ auth.callback.backToSignIn | polyglot }}',
      show: (el, s) => !!s.error,
      fontSize: 'Z2'
    }
  }
}
